import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Phone } from "lucide-react";
import { site } from "@/config/site";
import { SEO } from "@/components/site/SEO";
import { trackEvent } from "@/lib/tracking";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.89 1.22 3.09.15.2 2.11 3.22 5.1 4.51.71.31 1.27.49 1.7.63.72.23 1.37.2 1.88.12.58-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.83 9.83 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.82 9.82 0 0 1 9.88 9.89c0 5.45-4.44 9.88-9.89 9.88m8.42-18.29A11.8 11.8 0 0 0 12.04 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.9 11.9 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.9 0-3.18-1.24-6.16-3.48-8.4" />
  </svg>
);

export default function ThankYou() {
  useEffect(() => {
    trackEvent("thank_you_page_view");
  }, []);

  return (
    <>
      <SEO
        title="Thank You | My Blinds Company"
        description="Thank you — we've received your enquiry and will be in touch shortly."
        path="/thank-you"
        noindex
      />
      <section className="min-h-[70vh] flex items-center bg-warm-50" data-testid="thank-you-page">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center">
          <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 mb-6">
            <CheckCircle2 className="h-8 w-8 text-emerald-700" />
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl text-navy-900 tracking-tight">Thank you</h1>
          <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-600">
            We've received your enquiry. A member of the My Blinds Company team
            will contact you shortly to arrange your free measure and quotation.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Need us sooner? We're happy to talk right now.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <a
              href={site.phone.href}
              data-testid="thank-you-call"
              onClick={() => trackEvent("phone_click", { location: "thank_you" })}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-navy-800 hover:-translate-y-0.5"
            >
              <Phone className="h-4 w-4" /> Call Us — {site.phone.display}
            </a>
            <a
              href={`${site.whatsapp.href}?text=${encodeURIComponent("Hi My Blinds Company — I've just sent an enquiry and would like to chat.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="thank-you-whatsapp"
              onClick={() => trackEvent("whatsapp_click", { location: "thank_you" })}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:brightness-95 hover:-translate-y-0.5"
            >
              <WhatsAppIcon className="h-4 w-4" /> WhatsApp Us
            </a>
          </div>
          <p className="mt-10 text-sm text-slate-500">
            <Link to="/" className="font-semibold text-navy-900 hover:underline" data-testid="thank-you-home">
              Back to the homepage
            </Link>
          </p>
        </div>
      </section>
    </>
  );
}
